import { useEffect, useState } from 'react'
import { Check, Mic, Play, RotateCcw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'

interface Props {
  /** True while the browser SpeechRecognition session is active. */
  listening: boolean
  /**
   * True for a short tail after each voiced chunk. Drives the red waveform
   * animation - we only animate when the user is actually talking so the
   * mic doesn't stay "ringing" through silence.
   */
  speaking: boolean
  /** Finalized transcript so far. */
  transcript: string
  /** Current in-progress chunk that hasn't been finalised yet. */
  interim: string
  /** Error message from SpeechRecognition, if any. */
  error?: string | null
  /** Stop the recognition and keep the transcript (arm the Use button). */
  onStop: () => void
  /** Resume recognition without clearing the existing transcript. */
  onResume: () => void
  /** Discard transcript and start a fresh recognition session. */
  onRestart: () => void
  /** Discard transcript and hide the overlay. */
  onCancel: () => void
  /** User confirmed - push text into the input and close. */
  onUse: (text: string) => void
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function VoiceRecorderOverlay({
  listening,
  speaking,
  transcript,
  interim,
  error,
  onStop,
  onResume,
  onRestart,
  onCancel,
  onUse,
}: Props) {
  const [startedAt, setStartedAt] = useState(() => Date.now())
  const [elapsed, setElapsed] = useState('0:00')

  useEffect(() => {
    if (!listening) return
    setStartedAt(Date.now())
  }, [listening])

  useEffect(() => {
    if (!listening) return
    const tick = () => setElapsed(formatDuration(Date.now() - startedAt))
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [listening, startedAt])

  const combined = [transcript, interim].filter(Boolean).join(' ').trim()
  const hasText = combined.length > 0

  return (
    <div
      role="dialog"
      aria-label="Voice input"
      // Solid background (bg-card) so the overlay is opaque and does not
      // bleed into the chat content sitting behind it when opened with
      // messages in the transcript area.
      className="rounded-2xl border border-border bg-card p-3 shadow-xl"
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'relative flex size-10 shrink-0 items-center justify-center rounded-full',
            listening
              ? speaking
                ? 'bg-destructive/15 text-destructive'
                : 'bg-muted text-muted-foreground'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {listening && speaking ? (
            <>
              <span className="absolute inset-0 animate-ping rounded-full bg-destructive/30" />
              <span className="absolute inset-[-4px] animate-pulse rounded-full border border-destructive/40" />
            </>
          ) : null}
          <Mic className="relative size-5" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-foreground">
              {listening ? 'Recording…' : 'Recording stopped'}
            </span>
            {listening ? (
              <span className="font-mono text-[11px] text-muted-foreground">{elapsed}</span>
            ) : null}
          </div>
          {/*
            Fixed-height scroll region so the overlay never grows as the
            transcript lengthens. The input area below stays put.
          */}
          <div className="mt-1 max-h-[72px] overflow-y-auto pr-1 text-sm leading-snug text-foreground">
            {combined ? (
              <p>
                <span>{transcript}</span>
                {interim ? <span className="text-muted-foreground">{' '}{interim}</span> : null}
              </p>
            ) : (
              <p className="text-muted-foreground">
                {listening ? 'Speak now - your words will appear here.' : 'No speech detected yet.'}
              </p>
            )}
          </div>
          {error ? (
            <p className="mt-1 text-[11px] text-destructive">{error}</p>
          ) : null}
        </div>
      </div>
      {/*
        Icon-only action rail. Order is consistent across states so the
        Cancel icon never jumps position: Cancel · Restart · [Resume|Stop] · Use.
      */}
      <div className="mt-2 flex items-center justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onCancel}
          className="size-8 rounded-full text-muted-foreground hover:text-foreground"
          aria-label="Cancel"
          title="Cancel"
        >
          <X className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRestart}
          disabled={!hasText && !listening}
          className="size-8 rounded-full text-muted-foreground hover:text-foreground"
          aria-label="Restart recording"
          title="Restart"
        >
          <RotateCcw className="size-4" />
        </Button>
        {listening ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onStop}
            className="size-8 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/15 hover:text-destructive"
            aria-label="Stop recording"
            title="Stop"
          >
            <span className="size-3 rounded-sm bg-current" />
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onResume}
            className="size-8 rounded-full text-primary hover:bg-primary/10 hover:text-primary"
            aria-label="Resume recording"
            title="Resume"
          >
            <Play className="size-4 fill-current" />
          </Button>
        )}
        <Button
          type="button"
          size="icon"
          onClick={() => onUse(combined)}
          disabled={!hasText}
          className="size-8 rounded-full"
          aria-label="Use text"
          title="Use text"
        >
          <Check className="size-4" />
        </Button>
      </div>
    </div>
  )
}
